import { createSlice } from '@reduxjs/toolkit';
import { ADMIN_ROLE_UUID, USER_ROLE_UUID } from 'src/constants/user-role-constants';
import { isAuthorizedEmailDomain } from '../../utils/email-domain-validator';

// ----------------------------------------------------------------------

const initialState = {
  token: '',
  email: '',
  userRole: '',
  name: '',
  surname: '',
  communicationEmail: '',
  userAdditionalInfo: {},
  isAuthenticated: false,
  isRegistered: false,
  isInitialized: false,
  ip: '',
  registerStatus: '',
  loginStatus: '',
  createdAt: '',
  billingInfos: {},
  adminHasAccess: true,
  isVCValidator: false,
  isLanguageSkillsValidator: false,
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserEmail(state, action) {
      state.email = action.payload.email;
    },
    setAudit(state, action) {
      state.ip = action.payload.ip;
      state.registerStatus = action.payload.registerStatus;
      state.loginStatus = action.payload.loginStatus;
      state.createdAt = action.payload.createdAt;
    },
    clearAudit(state, action){
      state.ip = ""
    },
    setIsAgencyUser(state, action) {
      state.isAgencyUser = action.payload
    },
    setUserRegisterData(state, action) {
      state.isRegistered = true
      state.isAuthenticated = true
      state.isInitialized = true
      state.email = action.payload.email
      state.userRole = isAuthorizedEmailDomain(action.payload.email) ? ADMIN_ROLE_UUID : USER_ROLE_UUID
      state.name = action.payload.name
      state.surname = action.payload.surname
      state.languages = action.payload.languages && JSON.parse(action.payload.languages)
      state.nations = action.payload.nations && JSON.parse(action.payload.nations)
      state.country = action.payload.country
      state.isAgencyUser = action.payload.isAgencyUser
      state.communicationEmail = action.payload.communicationEmail
      state.id = action.payload.id
      state.userAdditionalInfo = {
        speaksFirst: action.payload.speaksFirst,
        learnEnglishAge: action.payload.learnEnglishAge,
        speaksHome: action.payload.speaksHome,
        yearsCurrentCountry : action.payload.yearsCurrentCountry,
        familyMembers : action.payload.familyMembers,
        havePets : action.payload.havePets,
        cityBorn : action.payload.cityBorn,
        stateBorn : action.payload.stateBorn,
        countryBorn : action.payload.countryBorn,
        mostCountryLive : action.payload.mostCountryLive,
        birthYear : action.payload.birthYear,
        educationLevel : action.payload.educationLevel,
        gender : action.payload.gender,
        translationExperience : action.payload.translationExperience,
        race : action.payload.race,
        previousExperience : action.payload.previousExperience,
        previousExperienceWhen : action.payload.previousExperienceWhen,
        exposeContent : action.payload.exposeContent,
        experienceArray : action.payload.experienceArray && JSON.parse(action.payload.experienceArray),
        workExperienceArray : action.payload.workExperienceArray && JSON.parse(action.payload.workExperienceArray),
        hearAboutUs: action.payload.hearAboutUs && JSON.parse(action.payload.hearAboutUs)
      }
      state.billingInfos = {
        currency: action.payload.currency,
        billingEmail: action.payload.billingEmail,
        billingCity: action.payload.billingCity,
        billingCountry: action.payload.billingCountry,
        billingAddress1: action.payload.billingAddress1,
        billingAddress2: action.payload.billingAddress2,
        billingAddressZip: action.payload.billingAddressZip,
        defaultPaymentTerm: action.payload.defaultPaymentTerm,
        defaultPaymentTermEmpty: action.payload.defaultPaymentTermEmpty,
        paymentMethod: action.payload.paymentMethod
      }
      state.isVCValidator = action.payload.isVCValidator
      state.isLanguageSkillsValidator = action.payload.isLanguageSkillsValidator
    },

    setUserInfo(state, action) {
      state.name = action.payload.name;
      state.surname = action.payload.surname;
      state.communicationEmail = action.payload.communicationEmail;
      state.id = action.payload.id;
      state.languages = action.payload.languages && JSON.parse(action.payload.languages);
      state.nations = action.payload.nations && JSON.parse(action.payload.nations);
      state.country = action.payload.country
      state.isVCValidator = action.payload.isVCValidator
      state.isLanguageSkillsValidator = action.payload.isLanguageSkillsValidator
    },
    setUserRole(state, action) {
      state.userRole = action.payload;
    },
    setIsInitialized(state) {
      state.isInitialized = true
    },
    setIsAuthenticated(state) {
      state.isAuthenticated = true
    },
    setIsRegistered(state, action) {
      state.isRegistered = action.payload
    },
    setUserAdditionalInfo(state, action) {
      state.userAdditionalInfo = {
        speaksFirst: action.payload.speaksFirst,
        birthYear: action.payload.birthYear,
        learnEnglishAge: action.payload.learnEnglishAge,
        speaksHome: action.payload.speaksHome,
        yearsCurrentCountry : action.payload.yearsCurrentCountry,
        familyMembers : action.payload.familyMembers,
        havePets : action.payload.havePets,
        cityBorn : action.payload.cityBorn,
        stateBorn : action.payload.stateBorn,
        countryBorn : action.payload.countryBorn,
        mostCountryLive : action.payload.mostCountryLive,
        educationLevel : action.payload.educationLevel,
        gender : action.payload.gender,
        translationExperience : action.payload.translationExperience,
        race : action.payload.race,
        previousExperience : action.payload.previousExperience,
        previousExperienceWhen : action.payload.previousExperienceWhen,
        exposeContent : action.payload.exposeContent,
        experienceArray : action.payload.experienceArray && JSON.parse(action.payload.experienceArray),
        workExperienceArray : action.payload.workExperienceArray && JSON.parse(action.payload.workExperienceArray)
      }
    },
    logoutUser(state){
      state.token= ''
      state.email = ''
      state.communicationEmail = ''
      state.userRole = ''
      state.name = ''
      state.isAuthenticated = false
      state.isRegistered = false
      state.isInitialized = false
      state.surname = ''
      state.ip = ''
      state.registerStatus = ''
      state.loginStatus = ''
      state.createdAt = ''
      state.userAdditionalInfo = {}
    },
    setToken(state, action) {
      state.token = action.payload
    },
    setBillingInfos(state, action) {
      state.billingInfos = action.payload
    },
    setAdminHasAccess(state, action) {
      state.adminHasAccess = action.payload
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setUserInfo,
  setUserRole,
  logoutUser,
  setIsRegistered,
  setBillingInfos,
  setIsAuthenticated,
  setIsInitialized,
  setToken,
  setUserEmail,
  setUserAdditionalInfo,
  setUserRegisterData,
  setAudit,
  clearAudit,
  setAdminHasAccess,
  setIsAgencyUser
} = slice.actions;
